import { useEffect, useRef, useState } from "react";
import {  useQuery } from "@apollo/client/react";
import { useAppContext } from "../provider/AppContextProvider";
import { DocumentNode } from "graphql";

/**
 * Hook to recursively fetch all paginated records using `useQuery` + `fetchMore`
 */
export const useFetchAllRecords = <T = any>(
    query: DocumentNode,
    collectionKey: string,
    variables: object = {}
) => {
    const { isInitializing } = useAppContext();

    const [allRecords, setAllRecords] = useState<T[]>([]);
    const [errorState, setErrorState] = useState<Error | null>(null);

    const hasMoreRef = useRef(true); // 🧠 persistent between renders
    const limit = 100; // 📝 pagination limit

    const { data, loading, error, fetchMore } = useQuery(query, {
        variables: { ...variables, limit, skip: 0 },
        notifyOnNetworkStatusChange: true,
        skip: isInitializing,
    });

    // Set initial records
    useEffect(() => {
        // console.log("Initial data fetched for", collectionKey);
        const items = ((data as any)?.[collectionKey]?.items as T[]) || [];
        if (items.length > 0) {
            setAllRecords(items);
        }
    }, [data, collectionKey]);

    // Load remaining pages recursively
    useEffect(() => {
        const loadRemaining = async () => {
            try {
                let skip = ((data as any)?.[collectionKey]?.items as T[] | undefined)?.length || 0;

                while (hasMoreRef.current) {
                    const { data: moreData } = await fetchMore({
                        variables: { ...variables, limit, skip },
                    });

                    const newItems = ((moreData as any)?.[collectionKey]?.items as T[]) || [];

                    if (newItems.length === 0 || newItems.length < limit) {
                        hasMoreRef.current = false; // 🛑 stop recursion
                    }

                    setAllRecords(prev => [...prev, ...newItems]);
                    skip += newItems.length;
                }
            } catch (fetchError) {
                const err = fetchError instanceof Error ? fetchError : new Error(String(fetchError));
                setErrorState(err);
            }
        };

        if (allRecords.length === limit && hasMoreRef.current) {
            // console.log("Loading more records...");
            loadRemaining();
        }
    }, [data, fetchMore, collectionKey, variables, limit, allRecords.length]);

    // console.log("All records fetched:", allRecords.length);
    return {
        allRecords,
        loading,
        error: errorState || error,
    };
};
export default useFetchAllRecords;